// Shared 3-step upload pipeline for every OT artifact.
//
//   1. POST /transit-orders/:otId/upload-url  → { url, storedKey }
//   2. PUT  url with the file bytes
//   3. POST <register endpoint>               (caller-provided; depends on kind)
//
// This hook owns steps (1) and (2); the caller hands a `register`
// callback that performs step (3) using the storedKey we hand back.
// Errors at any step propagate; the hook itself doesn't toast — that's
// the register-callback's mutation hook job.
//
// We back the pipeline with TanStack Query's `useMutation` so the
// `isUploading` flag is driven by the query client's lifecycle (which
// handles unmounts, races, and StrictMode double-invocation correctly)
// rather than ad-hoc `useState` we have to reset by hand.

import { useMutation } from "@tanstack/react-query"

import {
  createUploadUrl,
  putToPresignedUrl,
} from "@/features/transit/api/transit.api"
import type {
  OT,
  OTUploadKind,
  UploadUrlPayload,
} from "@/features/transit/api/types"

interface UploadOptions {
  otId: number | string
  kind: OTUploadKind
  file: File
  // Extra ids needed by certain kinds (slotId for slotDoc, otLotId for
  // cccDoc). The caller only sets the ones relevant to its kind — the
  // API rejects mismatched payloads.
  slotId?: UploadUrlPayload["slotId"]
  otLotId?: number
  // Step 3: takes the storedKey + filename and registers the artifact via
  // whatever endpoint the kind requires. Returns the refreshed OT detail
  // body so the caller can update its cache.
  register: (args: {
    name: string
    storedKey: string
    file: File
  }) => Promise<OT>
}

function fileExtension(name: string): string {
  const idx = name.lastIndexOf(".")
  if (idx < 0 || idx === name.length - 1) return "bin"
  return name.slice(idx + 1).toLowerCase()
}

async function runUpload(opts: UploadOptions): Promise<OT> {
  const presigned = await createUploadUrl(opts.otId, {
    kind: opts.kind,
    extension: fileExtension(opts.file.name),
    slotId: opts.slotId,
    otLotId: opts.otLotId,
  })
  await putToPresignedUrl(presigned.url, opts.file)
  return opts.register({
    name: opts.file.name,
    storedKey: presigned.storedKey,
    file: opts.file,
  })
}

export function useTransitUpload() {
  const mutation = useMutation({
    mutationFn: (opts: UploadOptions) => runUpload(opts),
  })

  const upload = (opts: UploadOptions) => mutation.mutateAsync(opts)

  return {
    upload,
    isUploading: mutation.isPending,
    // `progress` is no longer fine-grained — consumers only check the
    // boolean. Kept on the return shape so call-sites don't need updates.
    progress: mutation.isPending ? 50 : 0,
  }
}
